import { ref, watch, toValue, type MaybeRefOrGetter } from 'vue'
import { apiClient } from '@/api/client'
import { useToast } from 'primevue/usetoast'
import { scaricaBase64 } from '@/utils/download'

export interface CampoConfig {
  id: string
  tipo: 'TESTO' | 'NUMERO' | 'DATA' | 'SELECT' | 'CHECKBOX' | 'TEXTAREA' | 'MULTI_SELECT' | 'RADIO'
  label: string
  obbligatorio: boolean
  opzioni?: { valore: string; etichetta: string }[]
  placeholder?: string
  lunghezza?: number
}

export interface SezioneConfig {
  id: string
  titolo: string
  ordine: number
  zona?: string
  visibileSe: Regola | null
  campi: CampoConfig[]
}

export interface ConfermaConfig {
  messaggio: string
}

export interface AzioneConfig {
  id: string
  label: string
  icona?: string
  stile?: 'outlined' | 'text' | 'raised'
  posizione: 'TOP_BAR' | 'BOTTOM_BAR' | 'INLINE'
  handlerId: string
  visibileSe: Regola | null
  conferma?: ConfermaConfig
}

export type RegolaOp = '=' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'and' | 'or' | 'not'

export interface Regola {
  op: RegolaOp
  campo?: string
  valore?: unknown
  regole?: Regola[]
  regola?: Regola
}

export interface PaginaConfig {
  versione: number
  sezioniNascoste: string[]
  sezioniEsclusive?: Record<string, number[]>
  /** Opzioni della pagina attive di default per l'azienda (da `opzioniAttiveAziende` della riga globale). */
  opzioniAttive?: string[]
  sezioniAggiuntive: SezioneConfig[]
  azioniAggiuntive: AzioneConfig[]
  overrideAzioni: Record<string, string>
  hookAzioni: Record<string, string>
}

const defaultConfig = (): PaginaConfig => ({
  versione: 1,
  sezioniNascoste: [],
  sezioniEsclusive: {},
  sezioniAggiuntive: [],
  azioniAggiuntive: [],
  overrideAzioni: {},
  hookAzioni: {},
})

function evalRegola(regola: Regola, contesto: Record<string, unknown>): boolean {
  const { op, campo, valore, regole, regola: sottoregola } = regola
  switch (op) {
    case 'and':
      return (regole ?? []).every((r) => evalRegola(r, contesto))
    case 'or':
      return (regole ?? []).some((r) => evalRegola(r, contesto))
    case 'not':
      return sottoregola ? !evalRegola(sottoregola, contesto) : true
    case '=':
      return contesto[campo!] === valore
    case '!=':
      return contesto[campo!] !== valore
    case '>':
      return (contesto[campo!] as number) > (valore as number)
    case '<':
      return (contesto[campo!] as number) < (valore as number)
    case '>=':
      return (contesto[campo!] as number) >= (valore as number)
    case '<=':
      return (contesto[campo!] as number) <= (valore as number)
    case 'contains': {
      const arr = contesto[campo!]
      return Array.isArray(arr) ? arr.includes(valore) : false
    }
    default:
      return true
  }
}

interface RisultatoAzione {
  successo: boolean
  messaggio: string
  downloadUrl: string | null
  datiAggiuntivi: Record<string, unknown>
}

export function usePersonalizzazione(idPaginaInput: MaybeRefOrGetter<string>) {
  const config = ref<PaginaConfig>(defaultConfig())
  const loading = ref(false)
  const error = ref<string | null>(null)
  const toast = useToast()

  async function carica() {
    const idPagina = toValue(idPaginaInput)
    if (!idPagina) return
    loading.value = true
    error.value = null
    try {
      const { data } = await apiClient.get<PaginaConfig>(`/api/v2/pagine/${idPagina}/config`)
      config.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Errore caricamento configurazione'
      config.value = defaultConfig()
    } finally {
      loading.value = false
    }
  }

  function sezioneMostrata(idSezione: string): boolean {
    return !config.value.sezioniNascoste.includes(idSezione)
  }

  function azioniPerPosizione(posizione: AzioneConfig['posizione']): AzioneConfig[] {
    return config.value.azioniAggiuntive.filter((a) => a.posizione === posizione)
  }

  function valutaCondizione(
    regola: Regola | null | undefined,
    contesto: Record<string, unknown>,
  ): boolean {
    if (!regola) return true
    return evalRegola(regola, contesto)
  }

  /**
   * Esegue il plugin hook associato a hookKey (es. "post:salva") se configurato.
   * Se nessun hook è configurato per questa azienda/pagina, è un no-op silenzioso.
   *
   * Uso tipico nel pulsante Salva di una view:
   *   await eseguiHook('post:salva', aziendaId, 'azienda', { nome: form.nome })
   *
   * Il hook è configurato in configurazione_pagina.json_config.hookAzioni:
   *   "hookAzioni": { "post:salva": "AZIONE_AZIENDADEMO" }
   */
  async function eseguiHook(
    hookKey: string,
    entitaId?: number | null,
    entitaTipo?: string,
    extraPayload?: Record<string, unknown>,
  ): Promise<RisultatoAzione | null> {
    const handlerId = config.value.hookAzioni[hookKey]
    if (!handlerId) return null

    try {
      const { data } = await apiClient.post<RisultatoAzione>(`/api/v2/azioni/${handlerId}`, {
        entitaId: entitaId ?? null,
        entitaTipo: entitaTipo ?? '',
        payload: extraPayload ?? {},
      })

      if (data.successo) {
        if (data.messaggio) {
          toast.add({ severity: 'success', summary: 'Operazione completata', detail: data.messaggio, life: 4000 })
        }
        if (data.datiAggiuntivi?.base64 && data.datiAggiuntivi?.filename) {
          scaricaBase64(data.datiAggiuntivi.base64 as string, data.datiAggiuntivi.filename as string)
        } else if (data.downloadUrl) {
          window.open(data.downloadUrl, '_blank')
        }
      } else {
        toast.add({ severity: 'warn', summary: 'Attenzione', detail: data.messaggio, life: 5000 })
      }

      return data
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore esecuzione hook ' + hookKey
      toast.add({ severity: 'error', summary: 'Errore', detail: msg, life: 5000 })
      return null
    }
  }


  watch(() => toValue(idPaginaInput), carica, { immediate: true })

  return {
    config,
    loading,
    error,
    carica,
    sezioneMostrata,
    azioniPerPosizione,
    valutaCondizione,
    eseguiHook,
  }
}
